import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Charge,
  ChargeInput,
  Payment,
  PaymentInput,
  LateFeeRuleInput,
} from '@pirouette/shared';
import { chargeInputSchema, paymentInputSchema, lateFeeRuleInputSchema } from '@pirouette/shared';
import type { TablesInsert } from '@pirouette/db';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

// CHARGES ------------------------------------------------------------------

export interface ChargeWithAllocations extends Charge {
  allocated_cents: number;
  remaining_cents: number;
  account_kind: 'studio' | 'competition' | 'custom';
  account_name: string;
}

export function useFamilyCharges(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['charges', familyId],
    queryFn: async (): Promise<ChargeWithAllocations[]> => {
      const { data, error } = await supabase
        .from('charge_with_allocations')
        .select('*')
        .eq('family_id', familyId!)
        .order('due_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as ChargeWithAllocations[];
    },
  });
}

export function useCreateCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ChargeInput): Promise<Charge> => {
      const parsed = chargeInputSchema.parse(input);
      const { data, error } = await supabase
        .from('charges')
        // account_id is populated by the DB trigger added in migration 0023
        // (derived from family + category). The generated Insert type can't
        // model a trigger-supplied column, so the cast to the table's narrow
        // Insert type bridges that gap without erasing to `never`.
        .insert(stripUndefined(parsed) as TablesInsert<'charges'>)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (charge) => {
      void qc.invalidateQueries({ queryKey: ['charges', charge.family_id] });
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
    },
  });
}

export function useVoidCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; family_id: string }) => {
      const { error } = await supabase.from('charges').update({ status: 'void' }).eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['charges', args.family_id] });
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
    },
  });
}

// PAYMENTS -----------------------------------------------------------------

export function useFamilyPayments(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['payments', familyId],
    queryFn: async (): Promise<Payment[]> => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('family_id', familyId!)
        .order('paid_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useRecordManualPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      input: PaymentInput;
      charge_allocations: { charge_id: string; amount_cents: number }[];
    }) => {
      const parsed = paymentInputSchema.parse(args.input);
      // Atomic RPC — replaces the two-step client INSERT pattern whose
      // failure mode left orphan payment rows when the second insert
      // failed. The RPC validates the allocation total inside the same
      // transaction and rolls back both sides if anything goes wrong.
      // Sprint 12: account_id is now required. The DB also enforces
      // that every allocation's charge.account_id matches.
      const { data, error } = await supabase.rpc('record_payment', {
        p_family_id: parsed.family_id,
        p_account_id: parsed.account_id,
        p_amount_cents: parsed.amount_cents,
        p_method: parsed.method,
        // The RPC's text params accept NULL, but Supabase generates these
        // args as a non-null `string`. Coalesce undefined->null for the wire,
        // then strip the null with `!` to satisfy the too-narrow generated
        // type (the non-nullable-type-assertion-style rule prefers `!` over a
        // cast when an assertion only removes null/undefined).
        p_reference: (parsed.reference ?? null)!,
        p_notes: (parsed.notes ?? null)!,
        p_allocations: args.charge_allocations,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['charges', args.input.family_id] });
      void qc.invalidateQueries({ queryKey: ['payments', args.input.family_id] });
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
      void qc.invalidateQueries({ queryKey: ['account-balances'] });
    },
  });
}

// BILLING ACCOUNTS (Sprint 12) ---------------------------------------------

export interface BillingAccountRow {
  id: string;
  family_id: string;
  kind: 'studio' | 'competition' | 'custom';
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export function useFamilyAccounts(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['billing-accounts', familyId],
    queryFn: async (): Promise<BillingAccountRow[]> => {
      const { data, error } = await supabase
        .from('billing_accounts')
        .select('*')
        .eq('family_id', familyId!)
        .eq('active', true)
        .order('kind');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateBillingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      family_id: string;
      kind: 'studio' | 'competition' | 'custom';
      name: string;
    }) => {
      const { error } = await supabase.from('billing_accounts').insert(input);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['billing-accounts', args.family_id] });
    },
  });
}

export interface AccountBalanceRow {
  account_id: string;
  family_id: string;
  kind: 'studio' | 'competition' | 'custom';
  name: string;
  active: boolean;
  outstanding_cents: number;
  open_count: number;
  overdue_count: number;
}

export function useFamilyAccountBalances(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['account-balances', familyId],
    queryFn: async (): Promise<AccountBalanceRow[]> => {
      const { data, error } = await supabase
        .from('account_balance')
        .select('*')
        .eq('family_id', familyId!)
        .eq('active', true)
        .order('kind');
      if (error) throw error;
      return (data ?? []) as AccountBalanceRow[];
    },
  });
}

// FAMILY BALANCES ----------------------------------------------------------

export interface FamilyBalanceRow {
  family_id: string;
  outstanding_cents: number;
  open_count: number;
  overdue_count: number;
}

export function useAllFamilyBalances() {
  return useQuery({
    queryKey: ['family-balances'],
    queryFn: async (): Promise<FamilyBalanceRow[]> => {
      const { data, error } = await supabase
        .from('family_balance')
        .select('*')
        .order('outstanding_cents', { ascending: false });
      if (error) throw error;
      return (data ?? []) as FamilyBalanceRow[];
    },
  });
}

export function useFamilyBalance(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['family-balance', familyId],
    queryFn: async (): Promise<FamilyBalanceRow | null> => {
      const { data, error } = await supabase
        .from('family_balance')
        .select('*')
        .eq('family_id', familyId!)
        .maybeSingle();
      if (error) throw error;
      return (data as FamilyBalanceRow | null) ?? null;
    },
  });
}

// LATE FEE RULES -----------------------------------------------------------

export interface LateFeeRule {
  id: string;
  name: string;
  grace_period_days: number;
  fee_cents: number | null;
  fee_percent_bp: number | null;
  applies_to: string[];
  active: boolean;
}

export function useLateFeeRules() {
  return useQuery({
    queryKey: ['late-fee-rules'],
    queryFn: async (): Promise<LateFeeRule[]> => {
      const { data, error } = await supabase
        .from('late_fee_rules')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateLateFeeRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LateFeeRuleInput) => {
      const parsed = lateFeeRuleInputSchema.parse(input);
      const { error } = await supabase.from('late_fee_rules').insert(stripUndefined(parsed));
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['late-fee-rules'] });
    },
  });
}

export function useApplyLateFees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string): Promise<number> => {
      const { data, error } = await supabase.rpc('apply_late_fees', { p_rule_id: ruleId });
      if (error) throw error;
      return data ?? 0;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
      void qc.invalidateQueries({ queryKey: ['charges'] });
    },
  });
}
